'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { isInternalSurface } from '@/lib/internal-surfaces'
import { LazyFrosty } from './lazy-frosty'

const BOTTOM_BARS = '[data-testid="sticky-buy-bar"], [data-testid="sticky-buy-bar-desktop"], [data-testid="comparison-bar"]'

export function bottomBarClearance(bars: Element[]): number {
  return Math.ceil(bars.reduce((max, bar) => {
    if (bar.getAttribute('aria-hidden') === 'true') return max
    const rect = bar.getBoundingClientRect()
    return rect.width > 0 ? Math.max(max, rect.height) : max
  }, 0))
}

/** Keep the assistant above purchase controls without changing their placement. */
export function SitewideFrosty() {
  const pathname = usePathname()
  const wrapper = useRef<HTMLDivElement>(null)
  const suppressed = isInternalSurface(pathname) || pathname === '/embed' || pathname?.startsWith('/embed/')

  useEffect(() => {
    if (suppressed) return
    const host = wrapper.current
    if (!host) return
    let observed: Element[] = []
    let footerClearance: HTMLElement | null = null
    const measure = () => {
      const clearance = `${bottomBarClearance(observed)}px`
      host.style.setProperty('--frosty-bottom-clearance', clearance)
      footerClearance = document.querySelector<HTMLElement>('[data-frosty-footer-clearance]')
      footerClearance?.style.setProperty('--frosty-footer-clearance', clearance)
    }
    const resize = new ResizeObserver(measure)
    const refresh = () => {
      const bars = Array.from(document.querySelectorAll(BOTTOM_BARS))
      if (bars.length !== observed.length || bars.some((bar, i) => bar !== observed[i])) {
        resize.disconnect()
        observed = bars
        observed.forEach(bar => resize.observe(bar))
      }
      measure()
    }
    let frame = 0
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; refresh() })
    }
    const mutations = new MutationObserver(schedule)
    mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden'] })
    window.addEventListener('resize', measure)
    refresh()
    return () => { mutations.disconnect(); resize.disconnect(); cancelAnimationFrame(frame); window.removeEventListener('resize', measure); footerClearance?.style.removeProperty('--frosty-footer-clearance') }
  }, [suppressed])

  // Internal Operations and publisher embeds never mount the public assistant.
  if (suppressed) return null
  return <div ref={wrapper} className="hp26" data-frosty-sitewide><LazyFrosty /></div>
}
